import React, { useRef, useState } from "react";
import {
  Brand,
  Matter,
  MSWordFont,
  NumberedListFormat,
  PricingLevel,
  PricingType,
  Spinner,
} from "@design-system";
import {
  ApolloCache,
  DefaultContext,
  FetchResult,
  MutationFunctionOptions,
  useQuery,
} from "@apollo/client";
import { useBrand, useRestApi, useSettings } from "~/hooks";
import {
  GENERATE_PARENT_PROPOSAL,
  ProposalQueryFetchPolicy,
  useBrandsQuery,
} from "~/graphql/query";
import { ParentProposalExportPreviewModal } from "./parent-proposal-export-preview-modal";
import { GenerateParentProposalQuery, GenerateParentProposalQueryVariables } from "~/gql/graphql";
import { UpdateParentMatterMutation, UpdateParentMatterMutationVariables } from "~/gql/graphql";
import { TotalStrings } from "~/design-system/components/matter-editor/pricing-text-creators";
import { FIND_PARENT_MATTER_QUERY } from "./parent-matter-query";

type Props = {
  matter: Matter[];
  onClose: () => void;
  includeTasksInProposalOutput: boolean;
  includeRatesInProposalOutput: boolean;
  brandId: number;
  parentId: number;
  excludeTax: boolean;
  totals: TotalStrings<string>;
  updateMatter: (
    options?:
      | MutationFunctionOptions<
          UpdateParentMatterMutation,
          UpdateParentMatterMutationVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined
  ) => Promise<FetchResult<UpdateParentMatterMutation, Record<string, any>, Record<string, any>>>;
};

export const ParentMatterProposalContainer = ({
  matter,
  parentId,
  onClose,
  updateMatter,
  brandId,
  totals,
  excludeTax,
}: Props) => {
  const { brand } = useBrand(brandId);
  const { loading: allBrandsLoading, data: brandsData } = useBrandsQuery();
  const { findAllBrands: brands = [] } = brandsData || { findAllBrands: [] };
  const downloadRef = useRef<HTMLAnchorElement>();

  const {
    data,
    loading,
    refetch: refetchProposal,
  } = useQuery<GenerateParentProposalQuery, GenerateParentProposalQueryVariables>(
    GENERATE_PARENT_PROPOSAL,
    {
      fetchPolicy: ProposalQueryFetchPolicy.NoCache,
      variables: {
        parentId,
      },
    }
  );

  const [downloading, setDownloading] = useState(false);
  const restApi = useRestApi();
  const { loading: settingsLoading, settings } = useSettings();

  if (settingsLoading) {
    return <Spinner show />;
  }

  const currencySymbol = settings?.currency.symbol;

  const onDownload = async () => {
    setDownloading(true);
    try {
      const fileDownloader = await restApi.getCurrentParentProposalDocumentVersion(parentId);
      if (downloadRef.current) {
        await fileDownloader.download(downloadRef.current);
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {!loading && (
        <>
          <ParentProposalExportPreviewModal
            taxPercent={settings.taxLevelPercent} // TODO JS: multi practice areas don't seem to handle multi currency yet, tax should be updated when it does
            totals={totals}
            collated={{
              ...data?.generateParentProposal.collated!,
              pricingType: PricingType[data?.generateParentProposal.collated.pricingType!],
              pricingLevel: PricingLevel[data?.generateParentProposal.collated.pricingLevel!],
            }}
            excludeTax={excludeTax}
            currencySymbol={currencySymbol}
            taxLabel={settings.taxLabel}
            show={true}
            taxLevelPercent={settings.taxLevelPercent ?? 0}
            loading={loading || settingsLoading || allBrandsLoading}
            logo={brand?.organisationLogo || ""}
            footer={brand?.footer || ""}
            downloading={downloading}
            fontFamily={(brand?.proposalFont as MSWordFont) || "Calibri"}
            numberedListFormat={(brand?.numberedListFormat as NumberedListFormat) || "brackets"}
            lineBreakInLists={brand?.lineBreakInLists ?? false}
            proposals={data?.generateParentProposal.items?.map((proposal) => ({
              ...proposal,
              pricingType: PricingType[proposal.pricingType],
              pricingLevel: PricingLevel[proposal.pricingLevel],
            }))}
            onDownload={onDownload}
            onCancel={onClose}
            onClose={onClose}
            brands={(brands as Brand[]) || []}
            selectedBrand={brand.id}
            selectBrand={async (brandId) => {
              await updateMatter({
                variables: {
                  updateParentMatterId: parentId,
                  updateInput: {
                    brand: { connect: { id: brandId } },
                  },
                },
              });
              refetchProposal();
            }}
            includeTasksInProposalOutput={
              data?.generateParentProposal.items.some((x) => x.includeTasks) ?? false
            }
            includeRatesInProposalOutput={
              data?.generateParentProposal.items.some((x) => x.includeRatesInProposalOutput) ??
              false
            }
            includeFeeEarnersInProposalOutput={
              data?.generateParentProposal.items.some((x) => x.includeFeeEarnersInProposalOutput) ??
              true
            }
            setIncludeRatesInProposalOutputAtMatterLevel={async (include) => {
              await updateMatter({
                variables: {
                  updateParentMatterId: parentId,
                  updateInput: {
                    includeRatesInProposalOutput: { set: include },
                    matters: {
                      updateMany: [
                        {
                          data: { includeRatesInProposalOutput: { set: include } },
                          where: { parentMatterId: { equals: parentId } },
                        },
                      ],
                    },
                  },
                },
                update: (cache, { data }) => {
                  if (data?.updateParentMatter) {
                    cache.updateQuery(
                      { query: FIND_PARENT_MATTER_QUERY, variables: { id: parentId } },
                      (data) => {
                        if (!data?.findParentMatter) return data;
                        return {
                          findParentMatter: {
                            ...data.findParentMatter,
                            includeRatesInProposalOutput: include,
                          },
                        };
                      }
                    );
                  }
                },
              });
              refetchProposal();
            }}
            setIncludeTasksInProposalOutputAtMatterLevel={async (include) => {
              await updateMatter({
                variables: {
                  updateParentMatterId: parentId,
                  updateInput: { includeTasksInProposalOutput: { set: include } },
                },
                update: (cache, { data }) => {
                  if (data?.updateParentMatter) {
                    cache.updateQuery(
                      { query: FIND_PARENT_MATTER_QUERY, variables: { id: parentId } },
                      (data) => {
                        if (!data?.findParentMatter) return data;
                        return {
                          findParentMatter: {
                            ...data.findParentMatter,
                            includeTasksInProposalOutput: include,
                          },
                        };
                      }
                    );
                  }
                },
              });
              refetchProposal();
            }}
            setIncludeFeeEarnersInProposalOutputAtMatterLevel={async (include) => {
              await updateMatter({
                variables: {
                  updateParentMatterId: parentId,
                  updateInput: { includeFeeEarnersInProposalOutput: { set: include } },
                },
                update: (cache, { data }) => {
                  if (data?.updateParentMatter) {
                    cache.updateQuery(
                      { query: FIND_PARENT_MATTER_QUERY, variables: { id: parentId } },
                      (data) => {
                        if (!data?.findParentMatter) return data;
                        return {
                          findParentMatter: {
                            ...data.findParentMatter,
                            includeFeeEarnersInProposalOutput: include,
                          },
                        };
                      }
                    );
                  }
                },
              });
              refetchProposal();
            }}
            disableTaskToggle={
              !matter.map((x) => x.phases)?.some((x) => (x?.map((x) => x.tasks)?.length ?? 0) > 0)
            }
          />
          <a ref={(e) => (downloadRef.current = e || undefined)} />
        </>
      )}
    </>
  );
};
